/**
 * GraphQL Timesheet Service
 * Connects to AppSync for timesheet operations
 */

import { generateClient } from 'aws-amplify/api';
import {
  Project, Task, TimeEntry, WeeklyTimesheet, TimesheetStatus,
  ProjectStatus, CreateProjectInput, CreateTaskInput, CreateTimeEntryInput,
  UpdateTimeEntryInput, ProjectMember,
} from '../types/timesheet';
import { collectAcrossIdentities } from './identity';

let client: any = null;
const getClient = () => { if (!client) client = generateClient(); return client; };
const getAuthMode = () => ({ authMode: 'userPool' as const });
const getData = (r: any) => ('data' in r ? r.data : null);
const TMS_CACHE_TTL_MS = 30000;
const tmsCache = new Map<string, { timestamp: number; data: any }>();

function getCachedTms<T>(key: string): T | null {
  const cached = tmsCache.get(key);
  if (!cached || Date.now() - cached.timestamp > TMS_CACHE_TTL_MS) return null;
  return cached.data as T;
}

function setCachedTms<T>(key: string, data: T): void {
  tmsCache.set(key, { timestamp: Date.now(), data });
}

function clearTmsCache(prefix?: string): void {
  if (!prefix) {
    tmsCache.clear();
    return;
  }
  Array.from(tmsCache.keys()).forEach(key => {
    if (key.startsWith(prefix)) tmsCache.delete(key);
  });
}

// ─── Queries ───

const listProjectsQuery = /* GraphQL */ `
  query ListTMSProjects($limit: Int) {
    listTMSProjects(limit: $limit) { items { id name code clientName startDate endDate status billable createdAt updatedAt } nextToken }
  }
`;

const tasksByProjectQuery = /* GraphQL */ `
  query TMSTasksByProject($projectId: String!, $limit: Int) {
    tmsTasksByProject(projectId: $projectId, limit: $limit) { items { id projectId name description billable isActive isDefault createdAt } }
  }
`;

const membersByProjectQuery = /* GraphQL */ `
  query TMSMembersByProject($projectId: String!, $limit: Int) {
    tmsMembersByProject(projectId: $projectId, limit: $limit) { items { projectId employeeId employeeName assignedAt } }
  }
`;

const membersByEmployeeQuery = /* GraphQL */ `
  query TMSMembersByEmployee($employeeId: String!, $limit: Int) {
    tmsMembersByEmployee(employeeId: $employeeId, limit: $limit) { items { projectId employeeId employeeName assignedAt } }
  }
`;

const timesheetsByEmployeeQuery = /* GraphQL */ `
  query TMSTimesheetsByEmployee($employeeId: String!, $limit: Int) {
    tmsTimesheetsByEmployee(employeeId: $employeeId, limit: $limit) {
      items { id employeeId employeeName managerId weekStartDate weekEndDate status totalHours submittedAt approvedAt approvedBy returnedReason createdAt updatedAt }
    }
  }
`;

const timesheetsByStatusQuery = /* GraphQL */ `
  query TMSTimesheetsByStatus($status: String!, $limit: Int) {
    tmsTimesheetsByStatus(status: $status, limit: $limit) {
      items { id employeeId employeeName managerId weekStartDate weekEndDate status totalHours submittedAt approvedAt approvedBy returnedReason createdAt updatedAt }
    }
  }
`;

const entriesByTimesheetQuery = /* GraphQL */ `
  query TMSEntriesByTimesheet($weeklyTimesheetId: String!, $limit: Int) {
    tmsEntriesByTimesheet(weeklyTimesheetId: $weeklyTimesheetId, limit: $limit) {
      items { id employeeId employeeName weeklyTimesheetId projectId projectName taskId taskName date hours description billable createdAt updatedAt }
    }
  }
`;

// ─── Mutations ───

const createProjectMutation = /* GraphQL */ `
  mutation CreateTMSProject($input: CreateTMSProjectInput!) {
    createTMSProject(input: $input) { id name code clientName startDate endDate status billable createdAt updatedAt }
  }
`;

const updateProjectMutation = /* GraphQL */ `
  mutation UpdateTMSProject($input: UpdateTMSProjectInput!) {
    updateTMSProject(input: $input) { id name code clientName startDate endDate status billable createdAt updatedAt }
  }
`;

const createTaskMutation = /* GraphQL */ `
  mutation CreateTMSTask($input: CreateTMSTaskInput!) {
    createTMSTask(input: $input) { id projectId name description billable isActive isDefault createdAt }
  }
`;

const addMemberMutation = /* GraphQL */ `
  mutation AddTMSProjectMember($input: AddTMSProjectMemberInput!) {
    addTMSProjectMember(input: $input) { projectId employeeId employeeName assignedAt }
  }
`;

const removeMemberMutation = /* GraphQL */ `
  mutation RemoveTMSProjectMember($projectId: String!, $employeeId: String!) {
    removeTMSProjectMember(projectId: $projectId, employeeId: $employeeId) { projectId employeeId }
  }
`;

const createTimesheetMutation = /* GraphQL */ `
  mutation CreateTMSWeeklyTimesheet($input: CreateTMSWeeklyTimesheetInput!) {
    createTMSWeeklyTimesheet(input: $input) { id employeeId employeeName managerId weekStartDate weekEndDate status totalHours createdAt updatedAt }
  }
`;

const updateTimesheetMutation = /* GraphQL */ `
  mutation UpdateTMSWeeklyTimesheet($input: UpdateTMSWeeklyTimesheetInput!) {
    updateTMSWeeklyTimesheet(input: $input) { id employeeId employeeName managerId weekStartDate weekEndDate status totalHours submittedAt approvedAt approvedBy returnedReason createdAt updatedAt }
  }
`;

const createEntryMutation = /* GraphQL */ `
  mutation CreateTMSTimeEntry($input: CreateTMSTimeEntryInput!) {
    createTMSTimeEntry(input: $input) { id employeeId employeeName weeklyTimesheetId projectId projectName taskId taskName date hours description billable createdAt updatedAt }
  }
`;

const updateEntryMutation = /* GraphQL */ `
  mutation UpdateTMSTimeEntry($input: UpdateTMSTimeEntryInput!) {
    updateTMSTimeEntry(input: $input) { id hours description updatedAt }
  }
`;

const deleteEntryMutation = /* GraphQL */ `
  mutation DeleteTMSTimeEntry($id: ID!) {
    deleteTMSTimeEntry(id: $id) { id }
  }
`;

// ─── Service Class ───

export class GraphQLTimesheetService {

  // ── Projects ──

  static async listProjects(): Promise<Project[]> {
    try {
      const cached = getCachedTms<Project[]>('projects');
      if (cached) return cached;
      const result = await getClient().graphql({ query: listProjectsQuery, variables: { limit: 200 }, ...getAuthMode() } as any);
      const projects = getData(result)?.listTMSProjects?.items || [];
      setCachedTms('projects', projects);
      return projects;
    } catch (e) { console.error('[TMS] listProjects error:', e); return []; }
  }

  static async createProject(input: CreateProjectInput): Promise<Project | null> {
    try {
      const result = await getClient().graphql({ query: createProjectMutation, variables: { input: { ...input, status: ProjectStatus.ACTIVE } }, ...getAuthMode() } as any);
      const project = getData(result)?.createTMSProject || null;
      clearTmsCache();
      return project;
    } catch (e) { console.error('[TMS] createProject error:', e); throw e; }
  }

  static async updateProject(id: string, updates: Partial<Project>): Promise<Project | null> {
    // Only send fields that UpdateTMSProjectInput accepts
    const input: Record<string, any> = { id };
    if (updates.name !== undefined) input.name = updates.name;
    if (updates.code !== undefined) input.code = updates.code;
    if (updates.clientName !== undefined) input.clientName = updates.clientName;
    if (updates.startDate !== undefined) input.startDate = updates.startDate;
    if (updates.endDate !== undefined) input.endDate = updates.endDate;
    if (updates.status !== undefined) input.status = updates.status;
    if (updates.billable !== undefined) input.billable = updates.billable;
    
    console.log('[TMS] updateProject input:', JSON.stringify(input));
    try {
      const result = await getClient().graphql({ query: updateProjectMutation, variables: { input }, ...getAuthMode() } as any);
      const data = getData(result)?.updateTMSProject;
      console.log('[TMS] updateProject result:', data);
      clearTmsCache();
      return data || null;
    } catch (e: any) {
      // Amplify v6 throws on GraphQL errors but data may still be present
      console.error('[TMS] updateProject error:', e);
      if (e.data?.updateTMSProject) {
        console.log('[TMS] updateProject: data present despite error, treating as success');
        return e.data.updateTMSProject;
      }
      if (e.errors) console.error('[TMS] error details:', JSON.stringify(e.errors));
      throw e;
    }
  }

  // ── Tasks ──

  static async getTasksByProject(projectId: string): Promise<Task[]> {
    try {
      const cacheKey = `tasks|${projectId}`;
      const cached = getCachedTms<Task[]>(cacheKey);
      if (cached) return cached;
      const result = await getClient().graphql({ query: tasksByProjectQuery, variables: { projectId, limit: 100 }, ...getAuthMode() } as any);
      const tasks = getData(result)?.tmsTasksByProject?.items || [];
      setCachedTms(cacheKey, tasks);
      return tasks;
    } catch (e) { console.error('[TMS] getTasksByProject error:', e); return []; }
  }

  static async createTask(input: CreateTaskInput): Promise<Task | null> {
    try {
      const result = await getClient().graphql({ query: createTaskMutation, variables: { input: { ...input, isActive: true, isDefault: false } }, ...getAuthMode() } as any);
      const task = getData(result)?.createTMSTask || null;
      clearTmsCache(`tasks|${input.projectId}`);
      return task;
    } catch (e) { console.error('[TMS] createTask error:', e); throw e; }
  }

  // ── Project Members ──

  static async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
    try {
      const cacheKey = `projectMembers|${projectId}`;
      const cached = getCachedTms<ProjectMember[]>(cacheKey);
      if (cached) return cached;
      const result = await getClient().graphql({ query: membersByProjectQuery, variables: { projectId, limit: 200 }, ...getAuthMode() } as any);
      const members = getData(result)?.tmsMembersByProject?.items || [];
      setCachedTms(cacheKey, members);
      return members;
    } catch (e) { console.error('[TMS] getProjectMembers error:', e); return []; }
  }

  static async getEmployeeProjects(employeeId: string): Promise<ProjectMember[]> {
    try {
      const cacheKey = `employeeProjects|${employeeId}`;
      const cached = getCachedTms<ProjectMember[]>(cacheKey);
      if (cached) return cached;
      // Both email domains: project memberships created before the domain move are keyed
      // to the employee's old address.
      const projects = await collectAcrossIdentities<ProjectMember>(employeeId, async candidate => {
        const result = await getClient().graphql({ query: membersByEmployeeQuery, variables: { employeeId: candidate, limit: 100 }, ...getAuthMode() } as any);
        return getData(result)?.tmsMembersByEmployee?.items || [];
      });
      setCachedTms(cacheKey, projects);
      return projects;
    } catch (e) { console.error('[TMS] getEmployeeProjects error:', e); return []; }
  }

  static async addProjectMember(projectId: string, employeeId: string, employeeName: string): Promise<ProjectMember | null> {
    try {
      const result = await getClient().graphql({ query: addMemberMutation, variables: { input: { projectId, employeeId, employeeName } }, ...getAuthMode() } as any);
      const member = getData(result)?.addTMSProjectMember || null;
      clearTmsCache('projectMembers|');
      clearTmsCache('employeeProjects|');
      return member;
    } catch (e) { console.error('[TMS] addProjectMember error:', e); throw e; }
  }

  static async removeProjectMember(projectId: string, employeeId: string): Promise<void> {
    try {
      await getClient().graphql({ query: removeMemberMutation, variables: { projectId, employeeId }, ...getAuthMode() } as any);
      clearTmsCache('projectMembers|');
      clearTmsCache('employeeProjects|');
    } catch (e) { console.error('[TMS] removeProjectMember error:', e); throw e; }
  }

  // ── Weekly Timesheets ──

  static async getMyTimesheets(employeeId: string): Promise<WeeklyTimesheet[]> {
    try {
      const cacheKey = `myTimesheets|${employeeId}`;
      const cached = getCachedTms<WeeklyTimesheet[]>(cacheKey);
      if (cached) return cached;
      const timesheets = (await collectAcrossIdentities<WeeklyTimesheet>(employeeId, async candidate => {
        const result = await getClient().graphql({ query: timesheetsByEmployeeQuery, variables: { employeeId: candidate, limit: 100 }, ...getAuthMode() } as any);
        return getData(result)?.tmsTimesheetsByEmployee?.items || [];
      })).sort((a: any, b: any) => b.weekStartDate.localeCompare(a.weekStartDate));
      setCachedTms(cacheKey, timesheets);
      return timesheets;
    } catch (e) { console.error('[TMS] getMyTimesheets error:', e); return []; }
  }

  static async getTimesheetsByStatus(status: string): Promise<WeeklyTimesheet[]> {
    try {
      const cacheKey = `timesheetsByStatus|${status}`;
      const cached = getCachedTms<WeeklyTimesheet[]>(cacheKey);
      if (cached) return cached;
      const DEBUG = process.env.REACT_APP_DEBUG_TIMESHEET === 'true';
      
      if (DEBUG) {
        console.log('[Timesheet Debug] API call start', {
          operation: 'getTimesheetsByStatus',
          status,
          timestamp: new Date().toISOString()
        });
      }

      const result = await getClient().graphql({ query: timesheetsByStatusQuery, variables: { status, limit: 200 }, ...getAuthMode() } as any);
      const items = getData(result)?.tmsTimesheetsByStatus?.items || [];

      if (DEBUG) {
        console.log('[Timesheet Debug] API response', {
          operation: 'getTimesheetsByStatus',
          status,
          itemCount: items.length,
          sampleHasManagerId: items.length > 0 ? !!items[0].managerId : null
        });
      }

      setCachedTms(cacheKey, items);
      return items;
    } catch (e) {
      console.error('[TMS] getTimesheetsByStatus error:', e);
      return [];
    }
  }

  static async createWeeklyTimesheet(input: { employeeId: string; employeeName: string; managerId: string; weekStartDate: string; weekEndDate: string }): Promise<WeeklyTimesheet | null> {
    try {
      const result = await getClient().graphql({ query: createTimesheetMutation, variables: { input: { ...input, status: TimesheetStatus.DRAFT, totalHours: 0 } }, ...getAuthMode() } as any);
      const timesheet = getData(result)?.createTMSWeeklyTimesheet || null;
      clearTmsCache('myTimesheets|');
      clearTmsCache('timesheetsByStatus|');
      return timesheet;
    } catch (e) { console.error('[TMS] createWeeklyTimesheet error:', e); throw e; }
  }

  static async submitTimesheet(id: string): Promise<WeeklyTimesheet | null> {
    return this.updateTimesheet(id, { status: TimesheetStatus.SUBMITTED, submittedAt: new Date().toISOString() });
  }

  static async approveTimesheet(id: string, approvedBy: string): Promise<WeeklyTimesheet | null> {
    return this.updateTimesheet(id, { status: TimesheetStatus.APPROVED, approvedAt: new Date().toISOString(), approvedBy });
  }

  static async returnTimesheet(id: string, reason: string): Promise<WeeklyTimesheet | null> {
    return this.updateTimesheet(id, { status: TimesheetStatus.RETURNED, returnedReason: reason });
  }

  static async updateTimesheet(id: string, updates: Partial<WeeklyTimesheet>): Promise<WeeklyTimesheet | null> {
    // Only send fields that UpdateTMSWeeklyTimesheetInput accepts
    const input: Record<string, any> = { id };
    if (updates.status !== undefined) input.status = updates.status;
    if (updates.totalHours !== undefined) input.totalHours = updates.totalHours;
    if (updates.submittedAt !== undefined) input.submittedAt = updates.submittedAt;
    if (updates.approvedAt !== undefined) input.approvedAt = updates.approvedAt;
    if (updates.approvedBy !== undefined) input.approvedBy = updates.approvedBy;
    if (updates.returnedReason !== undefined) input.returnedReason = updates.returnedReason;
    
    console.log('[TMS] updateTimesheet input:', JSON.stringify(input));
    try {
      const result = await getClient().graphql({ query: updateTimesheetMutation, variables: { input }, ...getAuthMode() } as any);
      const data = getData(result)?.updateTMSWeeklyTimesheet;
      console.log('[TMS] updateTimesheet result:', data);
      clearTmsCache('myTimesheets|');
      clearTmsCache('timesheetsByStatus|');
      clearTmsCache(`entries|${id}`);
      return data || null;
    } catch (e: any) {
      // Amplify v6 throws on GraphQL errors but data may still be present
      console.error('[TMS] updateTimesheet error:', e);
      if (e.data?.updateTMSWeeklyTimesheet) {
        console.log('[TMS] updateTimesheet: data present despite error, treating as success');
        return e.data.updateTMSWeeklyTimesheet;
      }
      if (e.errors) console.error('[TMS] error details:', JSON.stringify(e.errors));
      throw e;
    }
  }

  // ── Time Entries ──

  static async getEntriesForTimesheet(weeklyTimesheetId: string): Promise<TimeEntry[]> {
    try {
      const cacheKey = `entries|${weeklyTimesheetId}`;
      const cached = getCachedTms<TimeEntry[]>(cacheKey);
      if (cached) return cached;
      const result = await getClient().graphql({ query: entriesByTimesheetQuery, variables: { weeklyTimesheetId, limit: 500 }, ...getAuthMode() } as any);
      const entries = getData(result)?.tmsEntriesByTimesheet?.items || [];
      setCachedTms(cacheKey, entries);
      return entries;
    } catch (e) { console.error('[TMS] getEntriesForTimesheet error:', e); return []; }
  }

  static async createTimeEntry(input: CreateTimeEntryInput & { employeeId: string; employeeName: string }): Promise<TimeEntry | null> {
    try {
      const result = await getClient().graphql({ query: createEntryMutation, variables: { input }, ...getAuthMode() } as any);
      const entry = getData(result)?.createTMSTimeEntry || null;
      clearTmsCache(`entries|${input.weeklyTimesheetId}`);
      clearTmsCache('myTimesheets|');
      clearTmsCache('timesheetsByStatus|');
      return entry;
    } catch (e) { console.error('[TMS] createTimeEntry error:', e); throw e; }
  }

  static async updateTimeEntry(input: UpdateTimeEntryInput): Promise<TimeEntry | null> {
    try {
      const result = await getClient().graphql({ query: updateEntryMutation, variables: { input }, ...getAuthMode() } as any);
      const entry = getData(result)?.updateTMSTimeEntry || null;
      clearTmsCache('entries|');
      clearTmsCache('myTimesheets|');
      clearTmsCache('timesheetsByStatus|');
      return entry;
    } catch (e) { console.error('[TMS] updateTimeEntry error:', e); throw e; }
  }

  static async deleteTimeEntry(id: string): Promise<void> {
    try {
      console.log('[TMS] deleteTimeEntry:', id);
      const result = await getClient().graphql({ query: deleteEntryMutation, variables: { id }, ...getAuthMode() } as any);
      
      // Check for GraphQL errors
      const resultAny = result as any;
      if (resultAny.errors && resultAny.errors.length > 0) {
        console.error('[TMS] deleteTimeEntry GraphQL errors:', JSON.stringify(resultAny.errors));
        throw new Error(resultAny.errors[0].message || 'Failed to delete entry');
      }
      clearTmsCache('entries|');
      clearTmsCache('myTimesheets|');
      clearTmsCache('timesheetsByStatus|');
      console.log('[TMS] deleteTimeEntry success:', id);
    } catch (e: any) {
      console.error('[TMS] deleteTimeEntry error:', e);
      if (e.errors) console.error('[TMS] deleteTimeEntry error details:', JSON.stringify(e.errors));
      throw e;
    }
  }
}
